import { OrderSalesPlans, SubOrder } from "@api/utils/normalizeOrder";
import { getStatusWithoutUnusual, LogisticHistoriesNumber } from "@constant/LogisticHistories";
import { CURRENT_SUB_ORDER } from "@constant/StorageKeyValue";
import { warehouseIdMap } from "@constant/WarehouseIdValue";
import useIntRouteParam from "@hooks/useIntRouteParam";
import SaleRuleType from "@page/Order/SaleRuleType";
import { hideLoading, showLoading } from "@redux/notifySlice";
import {
  changeOrderShippingStatus,
  downloadShippingLabel,
  fetchOrderDeploy,
  fetchRejectTransfer,
  fetchSubOrderCancel,
  orderState,
} from "@redux/orderSlice";
import Session from "@utils/SessionStorage";

import React, { useEffect, useMemo, useState } from "react";
import { CaretDownOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Row, Table, Timeline } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { useAppDispatch, useSelector } from "src/store";
import fontStyle from "src/styles/fontStyle";

import { ShippingTypeValueByNumber, transformShippingCode } from "../ShippingTypeValue";
import VendorLogisticsValue from "../VendorLogisticsValue";
import CVSPopup from "./CVSPopup";
import OrderPlanQty from "./OrderPlanQty";
import ReturnPopup from "./ReturnPopup";

const Wrapper = styled.div`
  margin-bottom: 61px;
`;

const CustomRow = styled(Row)`
  margin-bottom: 16px;
`;

const CustomButton = styled(Button)`
  margin-right: 10px;
`;

const Vendor = styled.div`
  margin-right: 14px;
  ${fontStyle("16px", "22px", "bold")};
`;

const HistoriesWrapper = styled.div`
  background: rgba(0, 0, 0, 0.75);
  border-radius: 2px;
  width: 300px;
  min-height: 200px;
  padding: 18px 14px;
  margin-top: 6px;
  display: none;
  position: absolute;
  top: 100%;
  z-index: 10;

  &::before {
    content: "";
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 3.5px 5px 3.5px;
    border-color: transparent transparent rgba(0, 0, 0, 0.75) transparent;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
  }

  .ant-timeline-item-content {
    display: flex;
  }
`;

const Text = styled.div<{ hoverLight?: boolean }>`
  margin-bottom: 16px;
  position: relative;
  ${fontStyle("14px", "22px")};

  &:hover {
    cursor: ${({ hoverLight }) => hoverLight && "pointer"};
    color: ${({ hoverLight, theme }) => (hoverLight ? "#EC6922" : theme.colorNeutral600)};

    ${HistoriesWrapper} {
      display: block;
    }
  }
`;

const SalePageLink = styled.a`
  color: ${({ theme }) => theme.colorSuccess500};
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colorSuccess500};
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`;

const TableWrapper = styled.div`
  margin-bottom: 20px;
`;

const TimelineItem = styled(Timeline.Item)`
  color: ${({ theme }) => theme.colorNeutral100};
  ${fontStyle("13px", "22px")};
`;

const InlineItem = styled.div`
  width: 100px;
`;

type Props = {
  subOrder: SubOrder;
};

export default function SubOrderTab(props: Props) {
  const { subOrder } = props;
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const orderId = useIntRouteParam("orderId", true);
  const [openPlanQty, setOpenPlanQty] = useState<boolean>(false);
  const [openCVS, setOpenCVS] = useState<boolean>(false);
  const [showReturn, setShowReturn] = useState<boolean>(false);

  const { isFetching } = useSelector(orderState);

  const ifCanReturn =
    (subOrder.logistic === VendorLogisticsValue.warehouse &&
      subOrder.shippingStatus === LogisticHistoriesNumber.PICKED_UP) ||
    (VendorLogisticsValue.warehouse && subOrder.shippingStatus === LogisticHistoriesNumber.FINISH);

  const columns: ColumnsType<OrderSalesPlans> = [
    {
      key: "salesPageName",
      dataIndex: "salesPageName",
      title: "銷售頁名稱",
      render: (
        value: string,
        record, // TODO 銷售頁後台連結
      ) => (
        <SalePageLink href={`/salespage/edit/${record.salesPageId}`} target="_blank">
          {value}
        </SalePageLink>
      ),
    },
    {
      key: "salesPlanName",
      dataIndex: "salesPlanName",
      title: "方案名稱",
    },
    {
      key: "qty",
      dataIndex: "qty",
      align: "center",
      title: "訂購數量",
      width: 100,
    },
    {
      key: "canceledQty",
      dataIndex: "canceledQty",
      align: "center",
      title: "已取消數量",
      width: 110,
    },
    {
      key: "backOrderQty",
      dataIndex: "backOrderQty",
      align: "center",
      title: "已退貨數量",
      width: 110,
    },
    {
      key: "optionsText",
      dataIndex: "optionsText",
      title: "選項內容",
    },
    {
      key: "price",
      dataIndex: "price",
      title: "方案售價",
      width: 100,
      render: (value: OrderSalesPlans["price"], record) => {
        return record.salesPlanRuleType === SaleRuleType.GIFT ? "N/A" : value;
      },
    },
    {
      key: "specialPrice",
      dataIndex: "price",
      title: "方案優惠價",
      width: 120,
      render: (value: OrderSalesPlans["price"], record) => {
        return record.salesPlanRuleType === SaleRuleType.GIFT ? "N/A" : value - record.salesDiscount;
      },
    },
    {
      key: "vipPrice",
      dataIndex: "price",
      title: "方案黑卡價",
      width: 120,
      render: (value: OrderSalesPlans["price"], record: OrderSalesPlans) => {
        return record.salesPlanRuleType === SaleRuleType.GIFT
          ? "N/A"
          : value - record.salesDiscount - record.memberGroupDiscount;
      },
    },
  ];

  const shippingText = useMemo(() => {
    switch (subOrder.shippingMethod) {
      case ShippingTypeValueByNumber.HOME_DELIVERY:
        return `${subOrder.code} ${subOrder.address}`;
      case ShippingTypeValueByNumber.SEVEN_ELEVEN:
        return `${transformShippingCode(subOrder.shippingMethod)}, ${subOrder.code} ${subOrder.storeName} ${subOrder.address}`;
      case ShippingTypeValueByNumber.CVS:
        return `${subOrder.code} ${subOrder.storeName} ${subOrder.address}`;
      default:
        return "";
    }
  }, [subOrder]);

  const reverseLogisticHistories = useMemo(() => {
    const reversed = [...subOrder.logisticHistories].reverse();
    return reversed.map((item) => (
      <TimelineItem key={item.updatedTime}>
        <InlineItem>{`${item.status}`}</InlineItem>
        <div>{`${moment(item.updatedTime).format("YYYY-MM-DD HH:mm:ss")}`}</div>
      </TimelineItem>
    ));
  }, [subOrder.logisticHistories]);

  const lastLogisticHistory = useMemo(() => {
    if (subOrder.logisticHistories.length === 0) return "";

    const lastHistory = subOrder.logisticHistories[subOrder.logisticHistories.length - 1];
    return `歷史紀錄: ${lastHistory.status} ${moment(lastHistory.updatedTime).format("YYYY-MM-DD HH:mm:ss")}`;
  }, [subOrder.logisticHistories]);

  const orderShippingNumberRender = () => {
    const orderIdThreshold = 1499768;

    switch (subOrder.shippingMethod) {
      case ShippingTypeValueByNumber.SEVEN_ELEVEN:
        return subOrder.id >= orderIdThreshold ? ` (UTC${subOrder.id})` : ` (UMS${subOrder.id})`;
      case ShippingTypeValueByNumber.CVS:
        return subOrder.id >= orderIdThreshold ? ` (UTC${subOrder.id})` : "";
      default:
        return "";
    }
  };

  const onThrow = () => {
    if (subOrder.shippingStatus === LogisticHistoriesNumber.OUT_OF_STOCK) {
      setOpenPlanQty(true);
      return;
    }

    if (subOrder.isCvsShippingFailed) {
      Session.setItem(CURRENT_SUB_ORDER, subOrder.id);
      setOpenCVS(true);
    } else {
      Modal.confirm({
        title: "你確定要重拋這筆訂單?",
        icon: <ExclamationCircleOutlined />,
        okText: "是",
        cancelText: "否",
        onOk() {
          dispatch(fetchOrderDeploy({ subOrderId: subOrder.id, mainOrderId: orderId }));
        },
      });
    }
  };

  const onClickReturn = () => {
    setShowReturn(true);
  };

  const onRejectTransfer = () => {
    Modal.confirm({
      title: "你確定要拒收這筆訂單?",
      icon: <ExclamationCircleOutlined />,
      okText: "是",
      cancelText: "否",
      onOk() {
        dispatch(fetchRejectTransfer({ subOrderId: subOrder.id, mainOrderId: orderId }));
      },
    });
  };

  const onCancelSubOrder = () => {
    if (
      subOrder.logistic === VendorLogisticsValue.warehouse &&
      subOrder.shippingStatus >= LogisticHistoriesNumber.DEDUCTED
    ) {
      Modal.warning({
        title: "藍田倉庫目前不支援取消訂單，請人工與倉庫確認",
      });
      return;
    }

    Modal.confirm({
      title: "你確定要取消這筆訂單?",
      icon: <ExclamationCircleOutlined />,
      content: `配送狀態: ${getStatusWithoutUnusual(subOrder.shippingStatus)}`,
      okText: "是",
      cancelText: "否",
      onOk() {
        dispatch(fetchSubOrderCancel({ subOrderId: subOrder.id, mainOrderId: orderId }));
      },
    });
  };

  const onChangeShippingStatus = () => {
    Modal.confirm({
      title: "你確定將狀態更改為[已取件]嗎?",
      icon: <ExclamationCircleOutlined />,
      okText: "是",
      cancelText: "否",
      onOk() {
        dispatch(
          changeOrderShippingStatus({
            subOrderId: subOrder.id,
            mainOrderId: orderId,
            statusCode: LogisticHistoriesNumber.PICKED_UP,
          }),
        );
      },
    });
  };
  const onDownloadShippingLabel = () => {
    dispatch(downloadShippingLabel(subOrder.id));
  };

  useEffect(() => {
    if (state && subOrder.id === Session.getItem(CURRENT_SUB_ORDER)) {
      setOpenCVS(true);
    }
  }, [state, subOrder.id]);

  useEffect(() => {
    if (isFetching) {
      dispatch(showLoading());
    } else {
      dispatch(hideLoading());
    }
  }, [isFetching, dispatch]);
  return (
    <Wrapper>
      <CustomRow align="middle">
        <Vendor>
          {subOrder.logisticShippingMethods[subOrder.logistic].name}
          {subOrder.vendorName.length === 1 && subOrder.logistic === VendorLogisticsValue.transfer && <span> - </span>}
          {subOrder.vendorName.length === 1 && subOrder.logistic === VendorLogisticsValue.transfer
            ? subOrder.vendorName[0]
            : ""}
        </Vendor>
        <CustomButton disabled={subOrder.logistic !== VendorLogisticsValue.warehouse} onClick={onThrow}>
          重拋單
        </CustomButton>
        <CustomButton disabled={subOrder.logistic !== VendorLogisticsValue.transfer} onClick={onRejectTransfer}>
          拒收
        </CustomButton>
        <CustomButton disabled={!ifCanReturn} onClick={onClickReturn}>
          退貨
        </CustomButton>
        <CustomButton disabled={!subOrder.canModifyPickup} onClick={onChangeShippingStatus}>
          已取件
        </CustomButton>
        <CustomButton
          disabled={subOrder.shippingStatus <= LogisticHistoriesNumber.CANCELED}
          onClick={onCancelSubOrder}
        >
          取消訂單
        </CustomButton>
        <CustomButton onClick={onDownloadShippingLabel}>托運單下載</CustomButton>
      </CustomRow>
      <Text>訂單資訊</Text>
      <OrderInfo>
        <Text>{`商品金額: $${subOrder.salesPlansPrice}`}</Text>
        <Text>{`運費: $${subOrder.shippingFee}`}</Text>
        <Text>{`總金額: $${subOrder.salesPlansPrice + subOrder.shippingFee}`}</Text>
        <Text>{`備註: ${subOrder.notes}`}</Text>
      </OrderInfo>
      <TableWrapper>
        <Table
          columns={columns}
          dataSource={subOrder.orderSalesPlans}
          pagination={false}
          scroll={{ x: "max-content", y: 400 }}
          rowKey="id"
        />
      </TableWrapper>
      <Text>寄送資訊</Text>
      <OrderInfo>
        <Text>{`收件人: ${subOrder.receiverName}`}</Text>
        <Text>{`收件電話: ${subOrder.receiverPhone}`}</Text>
        <Text>{`運送方式: ${shippingText}`}</Text>
        <Text>
          {`配送編號: ${subOrder.trackingNumber || "N/A"}`}
          {subOrder.trackingNumber &&
            subOrder.warehouseId != null &&
            warehouseIdMap[subOrder.warehouseId] &&
            ` (${warehouseIdMap[subOrder.warehouseId]})`}
        </Text>
        <Text>
          {`出貨單號: ${subOrder.shipperNumber || "N/A"}`}
          {orderShippingNumberRender()}
        </Text>
        <Text hoverLight>
          {lastLogisticHistory}
          <CaretDownOutlined />
          <HistoriesWrapper>
            <Timeline>{reverseLogisticHistories}</Timeline>
          </HistoriesWrapper>
        </Text>
        {subOrder.logistic === VendorLogisticsValue.preorder && (
          <Text>{`預期到貨日: ${moment(subOrder.deliveryDate * 1000).format("YYYY-MM-DD HH:mm:ss")}`}</Text>
        )}
      </OrderInfo>
      {openPlanQty && <OrderPlanQty order={subOrder} close={() => setOpenPlanQty(false)} />}
      {openCVS && <CVSPopup order={subOrder} close={() => setOpenCVS(false)} />}
      {showReturn && <ReturnPopup order={subOrder} onClose={() => setShowReturn(false)} showReturn={showReturn} />}
    </Wrapper>
  );
}
